import type { RouteT } from "@/route-types";
import type { InputT } from "@/types/database/forms";
import type { StatefulObservationViewT } from "@/types/database/observations";
import { useNavigate, useParams } from "@tanstack/react-router";
import useFormPage from "./useFormPage";

import { Route as ObservationCompletePatientRoute } from "@/routes/_app/diaries/$diaryId/observation/$observationId/entry/$entryId/complete/patient";
import { Route as ObservationCompleteStaffRoute } from "@/routes/_app/diaries/$diaryId/observation/$observationId/entry/$entryId/complete/staff";
import { Route as ObservationPageRoute } from "@/routes/_app/diaries/$diaryId/observation/$observationId/entry/$entryId/page/$pageIndex";
import { useAuth } from "../../../utils/auth";

interface Props {
	route: RouteT;
	pageCount: number;
	inputs: InputT[];
	nextEntry: StatefulObservationViewT["next_entry"];
	patientId: string;
}

const useObservationFormPage = (props: Props) => {
	const navigate = useNavigate();
	const auth = useAuth();

	const { diaryId, observationId, entryId, pageIndex } = useParams({
		strict: false,
	});

	const isMyDiary = props.patientId === auth.id;

	const nextPageFn = (newPageIndex: number) => {
		navigate({
			to: props.route,
			params: { diaryId, observationId, entryId, pageIndex: newPageIndex },
		});
	};

	const prevPageFn = (newPageIndex: number) => {
		navigate({
			to: props.route,
			params: { diaryId, observationId, entryId, pageIndex: newPageIndex },
		});
	};

	// Simplified successFn - we already have observationId from params
	const successFn = () => {
		if (observationId) {
			navigate({
				to: ObservationPageRoute.to,
				params: {
					diaryId: diaryId || "",
					observationId: observationId || "",
					entryId: entryId || "",
					pageIndex: pageIndex || "0",
				},
			});
		}
	};

	// Simplified completeFn - we already have observationId from params
	const completeFn = () => {
		if (diaryId && observationId && entryId) {
			if (isMyDiary) {
				navigate({
					to: ObservationCompletePatientRoute.to,
					params: {
						diaryId,
						observationId,
						entryId,
					},
				});
			} else {
				navigate({
					to: ObservationCompleteStaffRoute.to,
					params: {
						diaryId,
						observationId,
						entryId,
					},
				});
			}
		}
	};

	// Create a custom hook props object that matches the expected interface
	const formPageProps = {
		inputs: props.inputs,
		route: props.route,
		pageCount: props.pageCount,
		nextEntry: props.nextEntry,
		formType: "observation" as const,
		nextPageFn,
		prevPageFn,
		successFn,
		completeFn,
	};

	const { form, onSubmit, prevPage } = useFormPage(formPageProps);

	// Create a nextPage function that calls nextPageFn with the next page index
	const nextPage = () => {
		const currentPageIndex = Number(pageIndex) || 0;
		nextPageFn(currentPageIndex + 1);
	};

	return {
		form,
		onSubmit,
		prevPage,
		nextPage,
	};
};

export default useObservationFormPage;
